import React, { useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button, Form, Input, Modal } from "antd";
import type { ColumnsType } from "antd/es/table";
import { PlusOutlined, SearchOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons";

import DataTable from "../../../../components/shared/DataTable";
import StatusTag from "../../../../components/shared/StatusTag";
import { useNotifications } from "../../../../context/NotificationContext";
import { Patient } from "../../../../types/healthcare/PatientTypes";
import {
	VitalSignField,
	VitalSignPayload,
	VitalSignRecord,
} from "../../../../types/healthcare/VitalSignTypes";
import {
	listVitalSigns,
	createVitalSign,
	updateVitalSign,
	deleteVitalSign,
	getVitalSignsSchema,
} from "../../../../api/healthcare/vitalSignsApi";
import { VitalsReading, buildVitalsReadings } from "../../../../utils/healthcare/vitalsReadings";
import VitalsForm, { VitalsFormValues } from "./submodals/VitalsForm";

dayjs.extend(relativeTime);

const SOURCE_LABELS: Record<string, string> = {
	manual_entry: "Manual entry",
	manual: "Manual",
	md_import: "MD import",
	treatment_plan_task: "Treatment plan",
};
const sourceLabel = (source: string) => SOURCE_LABELS[source] ?? source;

interface VitalsTabProps {
	patient: Patient;
}

const VitalsTab: React.FC<VitalsTabProps> = ({ patient }) => {
	const { showError, showSuccess } = useNotifications();
	const queryClient = useQueryClient();
	const [form] = Form.useForm<VitalsFormValues>();

	const [filterValue, setFilterValue] = useState<string>("");
	const [modalOpen, setModalOpen] = useState<boolean>(false);
	const [editing, setEditing] = useState<VitalSignRecord | null>(null);

	const { data: schema = [] } = useQuery({
		queryKey: ["vitalSignsSchema"],
		queryFn: getVitalSignsSchema,
		staleTime: 1000 * 60 * 60,
	});

	const { data: records = [], isLoading } = useQuery({
		queryKey: ["vitalSigns", patient.id],
		queryFn: () => listVitalSigns(patient.id),
		enabled: !!patient.id,
	});

	const invalidate = () => queryClient.invalidateQueries({ queryKey: ["vitalSigns", patient.id] });

	const closeModal = () => {
		setModalOpen(false);
		setEditing(null);
	};

	// Surface Laravel 422 field errors onto the matching Form.Item (values.<key>);
	// anything not tied to a field is shown via the notification banner.
	const handleMutationError = (err: unknown) => {
		if (axios.isAxiosError(err) && err.response?.status === 422) {
			const errors = err.response.data?.errors as Record<string, string[]> | undefined;
			if (errors) {
				const fields: { name: ["values", string]; errors: string[] }[] = [];
				const general: string[] = [];
				Object.entries(errors).forEach(([key, msgs]) => {
					if (key.startsWith("values."))
						fields.push({ name: ["values", key.slice("values.".length)], errors: msgs });
					else general.push(...msgs);
				});
				if (fields.length) form.setFields(fields);
				if (general.length) showError(general.join(" "));
				else if (!fields.length) showError("Please correct the highlighted fields");
				return;
			}
		}
		showError("Failed to save reading");
	};

	const createMutation = useMutation({
		mutationFn: (payload: VitalSignPayload) => createVitalSign(patient.id, payload),
		onSuccess: () => {
			showSuccess("Reading saved");
			closeModal();
			invalidate();
		},
		onError: handleMutationError,
	});

	const updateMutation = useMutation({
		mutationFn: ({ id, payload }: { id: number; payload: VitalSignPayload }) =>
			updateVitalSign(patient.id, id, payload),
		onSuccess: () => {
			showSuccess("Reading updated");
			closeModal();
			invalidate();
		},
		onError: handleMutationError,
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => deleteVitalSign(patient.id, id),
		onSuccess: () => {
			showSuccess("Reading deleted");
			invalidate();
		},
		onError: () => showError("Failed to delete reading"),
	});

	const openAdd = () => {
		setEditing(null);
		setModalOpen(true);
	};

	const openEdit = (record: VitalSignRecord) => {
		setEditing(record);
		setModalOpen(true);
	};

	const handleDelete = (record: VitalSignRecord) => {
		Modal.confirm({
			title: "Delete reading",
			content: "Delete reading · this action cannot be undone.",
			okText: "Delete",
			okType: "danger",
			cancelText: "Cancel",
			onOk: () => deleteMutation.mutate(record.id),
		});
	};

	const handleSubmit = async () => {
		let raw: VitalsFormValues;
		try {
			raw = await form.validateFields();
		} catch {
			return;
		}
		const filtered: Record<string, number | string> = {};
		Object.entries(raw.values || {}).forEach(([key, value]) => {
			if (value !== null && value !== undefined && value !== "") filtered[key] = value;
		});
		if (Object.keys(filtered).length === 0) {
			showError("Enter at least one reading");
			return;
		}
		const payload: VitalSignPayload = {
			values: filtered,
			recorded_at: raw.recorded_at ? raw.recorded_at.toISOString() : undefined,
		};
		if (editing) updateMutation.mutate({ id: editing.id, payload });
		else createMutation.mutate(payload);
	};

	const initialValues: VitalsFormValues = {
		recorded_at: editing ? dayjs(editing.recorded_at) : dayjs(),
		values: editing ? { ...editing.values } : {},
	};

	const filtered = records.filter((record) => {
		if (!filterValue) return true;
		const q = filterValue.toLowerCase();
		const dateStr = dayjs(record.recorded_at).format("DD MMM YYYY HH:mm").toLowerCase();
		const src = sourceLabel(record.source).toLowerCase();
		const vals = Object.entries(record.values)
			.map(([k, v]) => `${k} ${v}`)
			.join(" ")
			.toLowerCase();
		return dateStr.includes(q) || src.includes(q) || vals.includes(q);
	});

	const columns: ColumnsType<VitalSignRecord> = [
		{
			title: "Recorded",
			dataIndex: "recorded_at",
			key: "recorded_at",
			width: 190,
			defaultSortOrder: "descend" as const,
			sorter: (a, b) => dayjs(a.recorded_at).valueOf() - dayjs(b.recorded_at).valueOf(),
			render: (_v, record) => (
				<span data-testid="vitals-row" style={{ display: "flex", flexDirection: "column" }}>
					<span>{dayjs(record.recorded_at).format("DD MMM YYYY HH:mm")}</span>
					<span style={{ color: "var(--mg-text-secondary)" }}>
						{dayjs(record.recorded_at).fromNow()}
					</span>
				</span>
			),
		},
		{
			title: "Readings",
			key: "readings",
			render: (_v, record) => {
				const readings = buildVitalsReadings(record, schema);
				if (readings.length === 0) {
					return (
						<span style={{ color: "var(--mg-text-secondary)" }}>No readings</span>
					);
				}
				return (
					<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
						{readings.map((r) => (
							<span
								key={r.key}
								className="mg-mono-num"
								style={{
									padding: "1px 6px",
									borderRadius: 3,
									border: r.outOfRange
										? "1px solid var(--mg-danger)"
										: "1px solid var(--mg-border)",
									background: r.outOfRange ? "var(--mg-danger-bg)" : "transparent",
									color: r.outOfRange ? "var(--mg-danger)" : "var(--mg-text)",
								}}
							>
								<span style={{ color: "var(--mg-text-secondary)" }}>{r.label} </span>
								{r.display}
								{r.unit ? ` ${r.unit}` : ""}
							</span>
						))}
					</div>
				);
			},
		},
		{
			title: "Actions",
			key: "actions",
			width: 90,
			fixed: "right" as const,
			render: (_v, record) => (
				<div style={{ display: "flex", gap: 2 }}>
					<Button
						type="text"
						size="small"
						icon={<EditOutlined />}
						data-testid="vitals-edit-btn"
						onClick={(e) => {
							e.stopPropagation();
							openEdit(record);
						}}
					/>
					<Button
						type="text"
						size="small"
						danger
						icon={<DeleteOutlined />}
						data-testid="vitals-delete-btn"
						onClick={(e) => {
							e.stopPropagation();
							handleDelete(record);
						}}
					/>
				</div>
			),
		},
	];

	return (
		<div data-testid="vitals-tab" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
			<div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
				<Button type="primary" icon={<PlusOutlined />} onClick={openAdd} data-testid="vitals-add-btn">
					Add reading
				</Button>
				<Input
					placeholder="Search readings…"
					prefix={<SearchOutlined />}
					value={filterValue}
					onChange={(e) => setFilterValue(e.target.value)}
					allowClear
				/>
			</div>

			<DataTable
				columns={columns}
				dataSource={filtered}
				rowKey={(r) => String(r.id)}
				loading={isLoading}
				onRowClick={openEdit}
				emptyTitle="No readings recorded"
				emptyDescription="Add a vital-sign reading to start tracking observations over time."
				emptyAction={{ label: "Add reading", onClick: openAdd }}
			/>

			{modalOpen && (
				<Modal
					title={editing ? "Edit reading" : "Add reading"}
					open={modalOpen}
					onCancel={closeModal}
					width={600}
					destroyOnClose
					footer={[
						<Button key="cancel" onClick={closeModal}>
							Cancel
						</Button>,
						<Button
							key="save"
							type="primary"
							loading={createMutation.isPending || updateMutation.isPending}
							onClick={handleSubmit}
						>
							Save
						</Button>,
					]}
				>
					<VitalsForm form={form} schema={schema} initialValues={initialValues} />
				</Modal>
			)}
		</div>
	);
};

export default VitalsTab;
